<script lang="ts">
    import { service } from "@frontend/services";
    import type { ApiResultOfCampaignsDTO, AuthenticatedRequestOfAddCampaignsDTO, AddCampaignsDTO } from "@frontend/walletapi";
    import { TypePromotion } from "@frontend/walletapi";
    import { onMount } from "svelte";
    import { translate, currentLanguage } from "../Translator";
    
    export let dismiss: () => void;
    
    let result: ApiResultOfCampaignsDTO | undefined = undefined;
    
    // Variable local para el monto
    let amountValue: number = 0;
    
    // Variables para el tiempo
    let days: number = 0;
    let hours: number = 0;
    let minutes: number = 0;
    let seconds: number = 0;
    
    // Variables para la configuración JSON estructurada
    let showAdvancedSettings: boolean = false;
    let promotions: Array<{
        type: string;
        ranges?: Array<{min: number, max: number | null, displayAmount: number}>;
        fixedAmount?: number;
    }> = [
        {
            type: "Recharge",
            ranges: [
                { min: 0, max: 19999, displayAmount: 0 },
                { min: 20000, max: 39999, displayAmount: 10000 },
                { min: 40000, max: null, displayAmount: 20000 }
            ]
        }
    ];

    let newPromotion: AddCampaignsDTO = {
        Type: TypePromotion.Manual,
        PromotionName: "",
        Description: "",
        StartAmount: { InCents: 0 },
        DepositRequired: false,
        Active: true,
        TotalDays: "0",
        ExpirationDate: new Date(),
        Settings: "",
        UserPromotional: [],
        Days: days,
        Hours: hours,
        Minutes: minutes,
        Seconds: seconds
    };
    
    // Hacer que newPromotion se actualice cuando cambien los valores de tiempo
    $: {
        newPromotion.Days = days;
        newPromotion.Hours = hours;
        newPromotion.Minutes = minutes;
        newPromotion.Seconds = seconds;
        newPromotion.TotalDays = convertToTimespan();
        newPromotion.StartAmount = { InCents: amountValue };
        newPromotion.Settings = generateSettingsJSON();
    }
    
    // Función para convertir días, horas, minutos y segundos a formato timespan
    function convertToTimespan(): string {
        // Asegurar que todos los valores sean números válidos
        const d = days || 0;
        const h = hours || 0;
        const m = minutes || 0;
        const s = seconds || 0;
        
        if (d === 0 && h === 0 && m === 0 && s === 0) {
            return "0";
        }
        
        if (d > 0) {
            // Formato con días: "DD.HH:MM:SS"
            return `${d}.${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        } else {
            // Formato sin días: "HH:MM:SS"
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
    }

    // Función para generar el JSON estructurado
    function generateSettingsJSON(): string {
        const settings: any = {
            promotions: promotions
        };

        return JSON.stringify(settings, null, 2);
    }

    // Función para agregar una nueva promoción
    function addPromotion() {
        promotions = [...promotions, {
            type: "Recharge",
            ranges: [
                { min: 0, max: 19999, displayAmount: 0 }
            ]
        }];
    }

    // Función para eliminar una promoción
    function removePromotion(index: number) {
        promotions = promotions.filter((_, i) => i !== index);
    }

    // Función para agregar un nuevo rango a una promoción específica
    function addRange(promotionIndex: number) {
        if (!promotions[promotionIndex].ranges) {
            promotions[promotionIndex].ranges = [];
        }
        promotions[promotionIndex].ranges!.push({ min: 0, max: null, displayAmount: 0 });
        promotions = [...promotions]; // Trigger reactivity
    }

    // Función para eliminar un rango de una promoción específica
    function removeRange(promotionIndex: number, rangeIndex: number) {
        if (promotions[promotionIndex].ranges) {
            promotions[promotionIndex].ranges = promotions[promotionIndex].ranges!.filter((_, i) => i !== rangeIndex);
            promotions = [...promotions]; // Trigger reactivity
        }
    }

    // Función para cambiar el tipo de promoción
    function changePromotionType(index: number, newType: string) {
        promotions[index].type = newType;
        if (newType === "Recharge") {
            promotions[index].ranges = [{ min: 0, max: 19999, displayAmount: 0 }];
            delete promotions[index].fixedAmount;
        } else {
            promotions[index].fixedAmount = 5000;
            delete promotions[index].ranges;
        }
        promotions = [...promotions]; // Trigger reactivity
    }
    
    async function handleSubmit() {
        try {
            // El monto ya está en centavos desde el input
            const request: AuthenticatedRequestOfAddCampaignsDTO = {
                Token: localStorage.getItem("token") || "",
                Data: newPromotion
            };
            
            result = await service.walletapi.addCampaigns(request);
            
            if (result?.Success) {
                dismiss();
            } else {
                console.error("Error adding campaign:", result?.ErrorMessage);
            }
        } catch (error) {
            console.error("Error adding campaign:", error);
        }
    }
</script>

<div class="compact-form">
    <h2 class="modal-title">Agregar Promoción</h2>
    
    <form on:submit|preventDefault={handleSubmit}>
        <div class="field">
            <label for="name">
                <i class="fas fa-tag"></i>
                Nombre de la Promoción
            </label>
            <input
                type="text"
                id="name"
                bind:value={newPromotion.PromotionName}
                placeholder="Ingrese el nombre de la promoción"
                required
            />
        </div>

        <div class="field">
            <label for="type">
                <i class="fas fa-list"></i>
                Tipo de Promoción
            </label>
            <select id="type" bind:value={newPromotion.Type} required>
                <option value={TypePromotion.Register}>Registro</option>
                <option value={TypePromotion.Birthday}>Cumpleaños</option>
                <option value={TypePromotion.HappyFriday}>Viernes Feliz</option>
                <option value={TypePromotion.WelcomeBonus}>Bienvenida</option>
                <option value={TypePromotion.DailyBonus}>Bono Diario</option>
                <option value={TypePromotion.Manual}>Manual</option>
                <option value={TypePromotion.Recharge}>Recarga</option>
            </select>
        </div>

        <div class="field">
            <label for="amount">
                <i class="fas fa-coins"></i>
                Cantidad de Créditos
            </label>
            <input
                type="number"
                id="amount"
                bind:value={amountValue}
                placeholder="Ingrese la cantidad de créditos"
                min="0"
                required
            />
        </div>

        <div class="field">
            <label for="totalDays">
                <i class="fas fa-clock"></i>
                Duración de la Promoción
            </label>
            <div class="time-inputs">
                <div class="time-input-group">
                    <label for="days">Días</label>
                    <input
                        type="number"
                        id="days"
                        bind:value={days}
                        placeholder="0"
                        min="0"
                        max="365"
                    />
                </div>
                <div class="time-input-group">
                    <label for="hours">Horas</label>
                    <input
                        type="number"
                        id="hours"
                        bind:value={hours}
                        placeholder="0"
                        min="0"
                        max="23"
                    />
                </div>
                <div class="time-input-group">
                    <label for="minutes">Minutos</label>
                    <input
                        type="number"
                        id="minutes"
                        bind:value={minutes}
                        placeholder="0"
                        min="0"
                        max="59"
                    />
                </div>
                <div class="time-input-group">
                    <label for="seconds">Segundos</label>
                    <input
                        type="number"
                        id="seconds"
                        bind:value={seconds}
                        placeholder="0"
                        min="0"
                        max="59"
                    />
                </div>
            </div>
            <div class="time-preview">
                <small>Formato: {newPromotion.TotalDays}</small>
            </div>
        </div>

        <div class="field">
            <label for="expirationDate">
                <i class="fas fa-calendar-alt"></i>
                Fecha de Expiración
            </label>
            <input
                type="date"
                id="expirationDate"
                bind:value={newPromotion.ExpirationDate}
                required
            />
        </div>

        {#if newPromotion.Type === 'Recharge'}
            <div class="field">
                <label for="settings">
                    <i class="fas fa-cog"></i>
                    Configuración de Promoción
                </label>
                <div class="settings-toggle">
                    <button type="button" class="ui small button" on:click={() => showAdvancedSettings = !showAdvancedSettings}>
                        <i class="fas fa-{showAdvancedSettings ? 'eye-slash' : 'eye'}"></i>
                        {showAdvancedSettings ? 'Ocultar Configuración' : 'Mostrar Configuración Avanzada'}
                    </button>
                </div>
                
                {#if showAdvancedSettings}
                    <div class="advanced-settings">
                        <div class="promotions-header">
                            <label>
                                <i class="fas fa-layer-group"></i>
                                Configuración de Promociones
                            </label>
                            <button type="button" class="ui small button" on:click={addPromotion}>
                                <i class="plus icon"></i>
                                Agregar Promoción
                            </button>
                        </div>

                        {#each promotions as promotion, promotionIndex}
                            <div class="promotion-item">
                                <div class="promotion-header">
                                    <span class="promotion-number">Promoción {promotionIndex + 1}</span>
                                    <button type="button" class="ui small red icon button" on:click={() => removePromotion(promotionIndex)}>
                                        <i class="trash icon"></i>
                                    </button>
                                </div>

                                <div class="field">
                                    <label for="promotionType{promotionIndex}">
                                        <i class="fas fa-list"></i>
                                        Tipo de Promoción
                                    </label>
                                    <select id="promotionType{promotionIndex}" bind:value={promotion.type} on:change={() => changePromotionType(promotionIndex, promotion.type)}>
                                        <option value="Recharge">Recharge (con rangos)</option>
                                        <option value="OtherPromoType">Otro tipo (valor fijo)</option>
                                    </select>
                                </div>

                                {#if promotion.type === "Recharge"}
                                    <div class="field">
                                        <label>
                                            <i class="fas fa-layer-group"></i>
                                            Rangos de Promoción
                                        </label>
                                        <div class="ranges-container">
                                            {#each promotion.ranges || [] as range, rangeIndex}
                                                <div class="range-row">
                                                    <div class="range-inputs">
                                                        <input
                                                            type="number"
                                                            placeholder="Mínimo"
                                                            bind:value={range.min}
                                                            min="0"
                                                        />
                                                        <span class="range-separator">-</span>
                                                        <input
                                                            type="number"
                                                            placeholder="Máximo"
                                                            bind:value={range.max}
                                                            min="0"
                                                        />
                                                        <span class="range-separator">→</span>
                                                        <input
                                                            type="number"
                                                            placeholder="Monto"
                                                            bind:value={range.displayAmount}
                                                            min="0"
                                                        />
                                                    </div>
                                                    <button type="button" class="ui small red icon button" on:click={() => removeRange(promotionIndex, rangeIndex)}>
                                                        <i class="trash icon"></i>
                                                    </button>
                                                </div>
                                            {/each}
                                            <button type="button" class="ui small button" on:click={() => addRange(promotionIndex)}>
                                                <i class="plus icon"></i>
                                                Agregar Rango
                                            </button>
                                        </div>
                                    </div>
                                {:else}
                                    <div class="field">
                                        <label for="fixedAmount{promotionIndex}">
                                            <i class="fas fa-dollar-sign"></i>
                                            Monto Fijo
                                        </label>
                                        <input
                                            type="number"
                                            id="fixedAmount{promotionIndex}"
                                            bind:value={promotion.fixedAmount}
                                            placeholder="Ingrese el monto fijo"
                                            min="0"
                                        />
                                    </div>
                                {/if}
                            </div>
                        {/each}


                    </div>
                {/if}
            </div>
        {/if}

        <div class="two fields checkboxes">
            <div class="field">
                <label class="checkbox-label">
                    <input
                        type="checkbox"
                        bind:checked={newPromotion.DepositRequired}
                    />
                    <i class="fas fa-money-bill-wave"></i>
                    Depósito Requerido
                </label>
            </div>
            <div class="field">
                <label class="checkbox-label">
                    <input
                        type="checkbox"
                        bind:checked={newPromotion.Active}
                    />
                    <i class="fas fa-toggle-on"></i>
                    Activa
                </label>
            </div>
        </div>

        <div class="button-row">
            <button type="button" class="ui button" on:click={dismiss}>
                Cancelar
            </button>
            <button type="submit" class="ui green button">
                Agregar
            </button>
        </div>
    </form>
</div>

<style>
    .compact-form {
        max-width: 350px;
        max-height: 80vh;
        margin: 0 auto;
        padding: 0.3rem;
        background: white;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        overflow-y: auto;
        overflow-x: hidden;
    }

    .compact-form::-webkit-scrollbar {
        width: 6px;
    }

    .compact-form::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }

    .compact-form::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
    }

    .compact-form::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
    }

.modal-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    text-align: center;
    margin-bottom: 0.4rem;
    padding-bottom: 0.3rem;
    border-bottom: 1px solid #e1e5e9;
}

form {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.field {
    margin-bottom: 0.2rem;
}

.field label {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.15rem;
    font-size: 0.8rem;
}

.field label i {
    color: #666;
    font-size: 0.75rem;
}

.field input {
    width: 100%;
    padding: 0.2rem 0.25rem;
    border: 1px solid #ddd;
    border-radius: 3px;
    font-size: 0.8rem;
    transition: all 0.2s ease;
    background: #fafbfc;
}

.field input:focus {
    outline: none;
    border-color: #2185d0;
    background: white;
    box-shadow: 0 0 0 2px rgba(33, 133, 208, 0.1);
}

.field select {
    width: 100%;
    padding: 0.2rem 0.25rem;
    border: 1px solid #ddd;
    border-radius: 3px;
    font-size: 0.8rem;
    transition: all 0.2s ease;
    background: #fafbfc;
}

.field select:focus {
    outline: none;
    border-color: #2185d0;
    background: white;
    box-shadow: 0 0 0 2px rgba(33, 133, 208, 0.1);
}



.settings-toggle {
    margin-bottom: 0.3rem;
}

.advanced-settings {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 3px;
    padding: 0.3rem;
    margin-top: 0.3rem;
}

.promotions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.3rem;
}

.promotions-header label {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    font-weight: 600;
    color: #333;
    font-size: 0.8rem;
}

.promotions-header label i {
    color: #666;
    font-size: 0.75rem;
}

.promotion-item {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 3px;
    padding: 0.3rem;
    margin-bottom: 0.3rem;
}

.promotion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.3rem;
    padding-bottom: 0.3rem;
    border-bottom: 1px solid #e9ecef;
}

.promotion-number {
    font-weight: 600;
    color: #333;
    font-size: 0.8rem;
}

.ranges-container {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.range-row {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.3rem;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 3px;
}

.range-inputs {
    display: flex;
    align-items: center;
    gap: 0.15rem;
    flex: 1;
}

.range-inputs input {
    width: 70px;
    font-size: 0.7rem;
    padding: 0.15rem 0.2rem;
    border: 1px solid #ddd;
    border-radius: 2px;
}

.range-separator {
    font-weight: bold;
    color: #666;
    font-size: 0.7rem;
}



.time-inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.3rem;
    margin-bottom: 0.3rem;
}

.time-input-group {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
}

.time-input-group label {
    font-size: 0.65rem;
    font-weight: 500;
    color: #666;
}

.time-input-group input {
    width: 100%;
    font-size: 0.75rem;
    padding: 0.15rem 0.2rem;
}

.time-preview {
    text-align: center;
    padding: 0.2rem;
    background: #f8f9fa;
    border-radius: 2px;
    border: 1px solid #e9ecef;
}

.time-preview small {
    color: #6c757d;
    font-style: italic;
    font-size: 0.65rem;
}

.two.fields {
    display: flex;
    gap: 0.3rem;
}

.two.fields .field {
    flex: 1;
    min-width: 0;
}

.two.fields.checkboxes {
    display: flex;
    gap: 0.3rem;
    margin-bottom: 0.1rem;
}

.two.fields.checkboxes .field {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
}

.checkbox-label {
    font-weight: 400;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 0.2rem;
}

.button-row {
    display: flex;
    gap: 0.3rem;
    margin-top: 0.4rem;
}

.button-row .button {
    flex: 1;
    min-width: 0;
    font-size: 0.8rem;
    padding: 0.3rem 0;
}

@media (max-width: 480px) {
    .compact-form {
        max-width: 98vw;
        padding: 0.6rem 0.15rem 0.6rem 0.15rem;
    }
    .modal-title {
        font-size: 1rem;
    }
    .button-row .button {
        font-size: 0.85rem;
        padding: 0.35rem 0;
    }
    .time-inputs {
        grid-template-columns: 1fr;
    }
}
</style>
